import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { supabase } from '@/lib/supabase'

// =============================================================================
// UsageOverview
//
// KPI cards + 30-day usage chart + by-endpoint bar chart for one org.
// Shared between the AccountOverviewPage (landing) and any future surface
// that wants to show "usage at a glance" in a single block.
//
// Pure presentational component — caller passes orgId + activeKeyCount;
// this component loads the usage summary via the RPC and renders.
// =============================================================================

interface DailyPoint { day: string; count: number }
interface EndpointPoint { endpoint: string; count: number }
interface UsageSummary {
  window_days: number
  today: number
  last_7_days: number
  last_30_days: number
  daily: DailyPoint[]
  by_endpoint: EndpointPoint[]
}

interface UsageOverviewProps {
  orgId: string | null
  activeKeyCount: number
  /** Hide the charts (show only KPI cards). Useful for very-small surfaces. */
  hideCharts?: boolean
}

export function UsageOverview({ orgId, activeKeyCount, hideCharts = false }: UsageOverviewProps) {
  const { data, loading } = useUsageSummary(orgId)
  return (
    <div className="flex flex-col gap-4">
      <KpiRow usage={data} loading={loading} activeKeyCount={activeKeyCount} />
      {!hideCharts && data && data.last_30_days > 0 ? <UsageCharts usage={data} /> : null}
    </div>
  )
}

function useUsageSummary(orgId: string | null) {
  const [data, setData] = useState<UsageSummary | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!orgId) {
      setData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data: row, error: err } = await supabase.rpc('usage_summary_for_org', {
      p_org_id: orgId,
      p_days: 30,
    })
    if (err) {
      setError(err.message)
      setData(null)
    } else {
      setData(row as UsageSummary)
    }
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load()
  }, [load])

  return { data, loading, error, refresh: load }
}

interface KpiRowProps {
  usage: UsageSummary | null
  loading: boolean
  activeKeyCount: number
}
function KpiRow({ usage, loading, activeKeyCount }: KpiRowProps) {
  const cards = [
    { label: 'Active keys', value: activeKeyCount },
    { label: 'Requests today', value: loading || !usage ? '—' : usage.today.toLocaleString() },
    { label: 'Last 7 days', value: loading || !usage ? '—' : usage.last_7_days.toLocaleString() },
    { label: 'Last 30 days', value: loading || !usage ? '—' : usage.last_30_days.toLocaleString() },
  ] as const
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] px-4 py-3"
        >
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">
            {c.label}
          </p>
          <p className="mt-1 text-[20px] font-semibold tabular-nums text-[var(--color-text-strong)]">
            {c.value}
          </p>
        </div>
      ))}
    </div>
  )
}

interface UsageChartsProps { usage: UsageSummary }
function UsageCharts({ usage }: UsageChartsProps) {
  // Pad the daily series so zero-traffic days show as 0 instead of skipping.
  const dailySeries = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of usage.daily) map.set(p.day.slice(0, 10), p.count)
    const series: { day: string; count: number; label: string }[] = []
    const today = new Date()
    for (let i = usage.window_days - 1; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      const iso = d.toISOString().slice(0, 10)
      series.push({
        day: iso,
        count: map.get(iso) ?? 0,
        label: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      })
    }
    return series
  }, [usage])

  const endpointSeries = useMemo(
    () =>
      usage.by_endpoint.map((p) => ({
        endpoint: p.endpoint.replace(/^v1\//, '/'),
        count: p.count,
      })),
    [usage],
  )

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ChartCard title={`Requests · last ${usage.window_days} days`}>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={dailySeries} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'var(--color-muted)' }}
              stroke="var(--color-border)"
              interval="preserveStartEnd"
              minTickGap={32}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--color-muted)' }}
              stroke="var(--color-border)"
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-elev-strong)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: 'var(--color-muted)' }}
              itemStyle={{ color: 'var(--color-text-strong)' }}
            />
            <Line
              type="monotone"
              dataKey="count"
              stroke="var(--color-accent)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="By endpoint">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={endpointSeries} layout="vertical" margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="var(--color-border)" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: 'var(--color-muted)' }}
              stroke="var(--color-border)"
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="endpoint"
              tick={{ fontSize: 11, fill: 'var(--color-muted)' }}
              stroke="var(--color-border)"
              width={120}
            />
            <Tooltip
              contentStyle={{
                background: 'var(--color-elev-strong)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                fontSize: 12,
              }}
              labelStyle={{ color: 'var(--color-muted)' }}
              itemStyle={{ color: 'var(--color-text-strong)' }}
              cursor={{ fill: 'var(--color-accent-soft)' }}
            />
            <Bar dataKey="count" fill="var(--color-accent)" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  )
}

interface ChartCardProps { title: string; children: React.ReactNode }
function ChartCard({ title, children }: ChartCardProps) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-elev)] p-4">
      <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--color-muted)] mb-2">
        {title}
      </p>
      {children}
    </div>
  )
}
